import {
  ELEMENT_TAG_MAPPING,
  ELEMENT_TYPE_MAPPING,
  EMBED_TAG_MAPPING,
  PROBLEMATIC_SYMBOL_SAFE_MAPPING,
} from "@routes/Article/components/ArticleParagraphElements/utils/constants";
import { Type } from "@typings/enums";
import { DOMNode } from "html-react-parser";

/**
 * Determines the type of an HTML element based on its tag name and class attributes.
 * Returns a specific type if the element matches predefined mappings.
 */
export const getElementType = (node: DOMNode) => {
  if (node.type !== "tag") return;

  const tagName = node.name;
  const className = node.attribs?.class;

  const classList = className ? className.split(" ") : [];

  if (
    ELEMENT_TAG_MAPPING[tagName] &&
    classList.find((list) => ELEMENT_TYPE_MAPPING[list] === Type.BoxTitle)
  ) {
    return Type.BoxTitle;
  }

  if (ELEMENT_TAG_MAPPING[tagName]) {
    return ELEMENT_TAG_MAPPING[tagName];
  }

  if (!className) return;

  return classList.map((list) => ELEMENT_TYPE_MAPPING[list]).filter(Boolean)[0];
};

/**
 * Determines the embed type of an HTML element based on its class attributes.
 * Returns a specific embed type if the element matches predefined mappings.
 */
export const getEmbedType = (node: DOMNode) => {
  if (node.type !== "tag") return;

  const className = node.attribs?.class;
  if (!className) return;

  const classList = className.split(" ");

  return classList.map((list) => EMBED_TAG_MAPPING[list]).filter(Boolean)[0];
};

/**
 * Sanitizes and replaces problematic symbols in a given text.
 * This function is useful for cleaning up text content before rendering it in the UI.
 */
export const replaceProblematicSymbols = (text: string) => {
  if (!text) return text;

  return Object.keys(PROBLEMATIC_SYMBOL_SAFE_MAPPING).reduce(
    (acc, key) => acc.split(key).join(PROBLEMATIC_SYMBOL_SAFE_MAPPING[key]),
    text
  );
};

/**
 * Helper function to extract the YouTube video ID from various YouTube URL formats.
 * @param {URL} The URL object containing the YouTube URL components.
 * @returns The YouTube video ID or undefined if not found.
 */
export const getYTVideoId = ({
  hostname,
  pathname,
  searchParams,
}: URL): string | undefined => {
  if (pathname === "/watch") return searchParams.get("v") || undefined;

  if (pathname.includes("/shorts/")) return pathname.split("/shorts/")[1];

  if (hostname === "youtu.be") return pathname.slice(1);
};

/**
 * Checks if a given URL is a YouTube URL.
 * @param {URL} The URL to check.
 * @returns True if the URL is a YouTube URL, false otherwise.
 */
export const isYouTubeUrl = ({ hostname }: URL): boolean => {
  return hostname.includes("youtube.com") || hostname.includes("youtu.be");
};

/**
 * Helper function to convert a standard YouTube URL into an embeddable URL format.
 * @param url The YouTube URL to convert.
 * @returns The embeddable YouTube URL.
 */
export const getYTEmbedUrl = (url: string): string => {
  const parsedUrl = new URL(url);
  const paramsString = parsedUrl.searchParams.toString();

  if (!isYouTubeUrl(parsedUrl)) return url;

  if (url.includes("/embed/")) return url;

  const videoId = getYTVideoId(parsedUrl);
  if (typeof videoId === "undefined") return url;

  const embedURL = new URL(`https://www.youtube.com/embed/${videoId}`);
  embedURL.search = paramsString;

  return embedURL.toString();
};
